import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { EASE, MaskedLines, PrimaryButton, GhostButton, scrollToId, useTheme } from "./shared";

const PALS = {
    night: { bg: [7, 7, 8], warm: [255, 224, 170], cool: [190, 225, 245], part: [244, 242, 239], glow: 0.55, band: 0.05, partA: 0.28 },
    day: { bg: [244, 241, 234], warm: [148, 98, 28], cool: [70, 98, 118], part: [24, 21, 17], glow: 0.32, band: 0.1, partA: 0.3 },
};

const lerpPal = (cur, tgt) => {
    for (const k of Object.keys(tgt)) {
        if (Array.isArray(tgt[k])) {
            for (let i = 0; i < 3; i++) cur[k][i] += (tgt[k][i] - cur[k][i]) * 0.06;
        } else {
            cur[k] += (tgt[k] - cur[k]) * 0.06;
        }
    }
};

const rgb = (c, a) => `rgba(${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])},${a})`;

const KineticCanvas = () => {
    const ref = useRef(null);
    const palRef = useRef(null);
    const { theme } = useTheme();

    useEffect(() => {
        if (!palRef.current) palRef.current = JSON.parse(JSON.stringify(PALS[theme]));
        const cur = palRef.current;
        const tgt = PALS[theme];
        const canvas = ref.current;
        const ctx = canvas.getContext("2d");
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        let w, h, dpr, raf, rods = [], particles = [];
        const mouse = { x: 0.5, y: 0.5, tx: 0.5, ty: 0.5 };

        const build = () => {
            dpr = Math.min(window.devicePixelRatio || 1, 2);
            w = canvas.clientWidth;
            h = canvas.clientHeight;
            canvas.width = w * dpr;
            canvas.height = h * dpr;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            const n = Math.max(18, Math.floor(w / 52));
            rods = Array.from({ length: n }, (_, i) => ({
                fx: (i + 0.5) / n,
                len: h * (0.28 + 0.22 * Math.abs(Math.sin(i * 1.7))),
                phase: i * 0.55,
                speed: 0.6 + (i % 4) * 0.22,
                amp: 0.28 + (i % 3) * 0.14,
                depth: 0.4 + ((i * 7) % 10) / 14,
                colKey: i % 4 === 3 ? "cool" : "warm",
            }));
            particles = Array.from({ length: 36 }, () => ({
                x: Math.random(), y: Math.random(), s: 0.4 + Math.random() * 1.1, v: 0.00004 + Math.random() * 0.00008,
            }));
        };

        const draw = (t) => {
            lerpPal(cur, tgt);
            mouse.x += (mouse.tx - mouse.x) * 0.04;
            mouse.y += (mouse.ty - mouse.y) * 0.04;
            ctx.fillStyle = rgb(cur.bg, 0.4);
            ctx.fillRect(0, 0, w, h);

            const px = (mouse.x - 0.5) * 46;
            const py = (mouse.y - 0.5) * 26;
            const anchorY = h * 0.16;

            for (const r of rods) {
                const ox = px * r.depth;
                const oy = py * r.depth * 0.5;
                const x = r.fx * w + ox;
                const ang = Math.sin(t * 0.00035 * r.speed + r.phase) * r.amp;
                const x2 = x + Math.sin(ang) * r.len;
                const y2 = anchorY + oy + Math.cos(ang) * r.len;
                const cArr = r.colKey === "cool" ? cur.cool : cur.warm;
                const grad = ctx.createLinearGradient(x, anchorY + oy, x2, y2);
                grad.addColorStop(0, rgb(cArr, 0.04));
                grad.addColorStop(1, rgb(cArr, 0.85));
                ctx.strokeStyle = grad;
                ctx.lineWidth = 1;
                ctx.shadowBlur = 12;
                ctx.shadowColor = rgb(cArr, cur.glow);
                ctx.beginPath();
                ctx.moveTo(x, anchorY + oy);
                ctx.lineTo(x2, y2);
                ctx.stroke();
                ctx.shadowBlur = 0;
                ctx.fillStyle = rgb(cArr, 0.9);
                ctx.beginPath();
                ctx.arc(x2, y2, 1.4, 0, Math.PI * 2);
                ctx.fill();
            }

            const bandX = (((t * 0.00004) % 1.3) - 0.15) * (w + 500) - 250;
            const band = ctx.createLinearGradient(bandX - 160, 0, bandX + 160, 0);
            band.addColorStop(0, rgb(cur.warm, 0));
            band.addColorStop(0.5, rgb(cur.warm, cur.band));
            band.addColorStop(1, rgb(cur.warm, 0));
            ctx.globalCompositeOperation = theme === "day" ? "source-over" : "lighter";
            ctx.fillStyle = band;
            ctx.fillRect(bandX - 160, 0, 320, h);

            for (const p of particles) {
                p.y -= p.v * 16;
                if (p.y < -0.02) { p.y = 1.02; p.x = Math.random(); }
                ctx.fillStyle = rgb(cur.part, cur.partA);
                ctx.fillRect(p.x * w, p.y * h, p.s, p.s);
            }
            ctx.globalCompositeOperation = "source-over";

            const vg = ctx.createRadialGradient(w / 2, h * 0.45, h * 0.2, w / 2, h * 0.5, h);
            vg.addColorStop(0, rgb(cur.bg, 0));
            vg.addColorStop(1, rgb(cur.bg, 0.9));
            ctx.fillStyle = vg;
            ctx.fillRect(0, 0, w, h);
        };

        build();
        if (reduced) {
            for (let i = 0; i < 300; i++) lerpPal(cur, tgt);
            draw(1200);
        } else {
            const loop = (t) => { draw(t); raf = requestAnimationFrame(loop); };
            raf = requestAnimationFrame(loop);
        }
        const onMove = (e) => { mouse.tx = e.clientX / w; mouse.ty = e.clientY / h; };
        const onResize = () => { build(); if (reduced) draw(1200); };
        window.addEventListener("mousemove", onMove, { passive: true });
        window.addEventListener("resize", onResize);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("resize", onResize);
        };
    }, [theme]);

    return <canvas ref={ref} data-testid="hero-kinetic-canvas" className="absolute inset-0 h-full w-full" />;
};

export const Hero = () => (
    <section data-testid="hero-section" className="relative flex min-h-[100svh] items-end overflow-hidden">
        <KineticCanvas />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#070708] to-transparent" />
        <div className="relative z-10 mx-auto w-full max-w-[1600px] px-6 pb-24 pt-40 md:px-12 md:pb-32">
            <motion.p
                data-testid="hero-overline"
                className="mb-8 font-mono-tech text-[11px] tracking-[0.4em] text-white/45"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.2, delay: 0.3 }}
            >
                KINETIC ART · LIGHT · MOTION — A DESIGN &amp; TECHNOLOGY STUDIO
            </motion.p>
            <h1 className="font-display text-[11vw] font-bold uppercase leading-[0.92] tracking-tighter text-[#F4F2EF] sm:text-7xl md:text-8xl lg:text-[7.5rem]">
                <MaskedLines immediate lines={["Spaces that move.", "Light that lives."]} delay={0.5} lineClassName="text-glow-warm" />
            </h1>
            <div className="mt-12 flex flex-col gap-10 md:flex-row md:items-end md:justify-between">
                <motion.p
                    data-testid="hero-subtext"
                    className="max-w-md text-base font-light leading-relaxed tracking-wide text-white/60 md:text-lg"
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.1, ease: EASE, delay: 1.5 }}
                >
                    We create kinetic art and intelligent lighting experiences that transform spaces into something unforgettable.
                </motion.p>
                <motion.div
                    className="flex flex-wrap gap-4"
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.1, ease: EASE, delay: 1.7 }}
                >
                    <PrimaryButton testId="hero-cta-create" onClick={() => scrollToId("contact")}>Create your installation</PrimaryButton>
                    <GhostButton testId="hero-cta-explore" onClick={() => scrollToId("experiences")}>Explore the world of motion</GhostButton>
                </motion.div>
            </div>
        </div>
        <motion.div
            className="absolute bottom-8 right-6 z-10 hidden flex-col items-center gap-3 md:right-12 md:flex"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.4, duration: 1 }}
        >
            <span className="font-mono-tech text-[9px] tracking-[0.4em] text-white/35 [writing-mode:vertical-lr]">SCROLL</span>
            <span className="h-16 w-px bg-white/30" style={{ animation: "scroll-cue 2.4s ease-in-out infinite" }} />
        </motion.div>
    </section>
);
